import type { childPartV1 } from "@/api/breakdownService";
import { TableCell } from "@mui/material";

// unit_type_id: 1 = fixed (THB/pcs), else = percent (%)
const formatUnitCost = (value: number | undefined, unitTypeId: number | undefined) =>
  unitTypeId === 1
    ? Number(value ?? 0).toFixed(2)
    : `${Number(value ?? 0).toFixed(2)}%`;

interface ChildPartProcessDataProps {
  row: childPartV1;
  isOpenOEMProcessColumn: boolean;
  OEMColumn: { id: number; name: string }[];
  isOpenSpareProcessColumn: boolean;
  spareColumn: { id: number; name: string }[];
  isOpenOEMPriceColumn: boolean;
  isOpenSparePriceColumn: boolean;
}

const ChildPartProcessData = ({
  row,
  isOpenOEMProcessColumn,
  OEMColumn,
  isOpenSpareProcessColumn,
  spareColumn,
  isOpenOEMPriceColumn,
  isOpenSparePriceColumn,
}: ChildPartProcessDataProps) => {
  return (
    <>
      <TableCell
        sx={{
          minWidth: 120,
          maxWidth: 120,
          borderRight: "1px solid #e0e0e0",
          borderLeft: "1px solid #e0e0e0",
          textAlign: "right",
          fontSize: "0.9rem",
        }}
      >
        {Number(row.total_material_cost ?? 0).toFixed(2)}
      </TableCell>
      <TableCell
        sx={{
          minWidth: 120,
          maxWidth: 120,
          borderRight: "1px solid #e0e0e0",
          borderLeft: "1px solid #e0e0e0",
          textAlign: "right",
          fontSize: "0.9rem",
        }}
      >
        {Number(row.child_part_process.oem_process_total ?? 0).toFixed(2)}
      </TableCell>
      {isOpenOEMProcessColumn && (
        <>
          {OEMColumn?.sort((a, b) => a.id - b.id).map((process) => (
            <TableCell
              key={process.id}
              sx={{
                minWidth: 120,
                maxWidth: 120,
                borderRight: "1px solid #e0e0e0",
                borderLeft: "1px solid #e0e0e0",
                textAlign: "right",
                fontSize: "0.9rem",
              }}
            >
              {Number(
                row.child_part_process.oem_process.find(
                  (p) => p.ms_oem_process_v1_id === process.id,
                )?.cost ?? 0,
              ).toFixed(2)}
            </TableCell>
          ))}
          <TableCell
            sx={{
              minWidth: 120,
              maxWidth: 120,
              borderRight: "1px solid #e0e0e0",
              borderLeft: "1px solid #e0e0e0",
              textAlign: "right",
              fontSize: "0.9rem",
            }}
          >
            {formatUnitCost(
              row.child_part_process.oem_process_admin,
              row.child_part_process.oem_process_admin_unit_type_id,
            )}
          </TableCell>
          <TableCell
            sx={{
              minWidth: 120,
              maxWidth: 120,
              borderRight: "1px solid #e0e0e0",
              borderLeft: "1px solid #e0e0e0",
              textAlign: "right",
              fontSize: "0.9rem",
            }}
          >
            {formatUnitCost(
              row.child_part_process.oem_process_profit,
              row.child_part_process.oem_process_profit_unit_type_id,
            )}
          </TableCell>
        </>
      )}
      <TableCell
        sx={{
          minWidth: 120,
          maxWidth: 120,
          borderRight: "1px solid #e0e0e0",
          borderLeft: "1px solid #e0e0e0",
          textAlign: "right",
          fontSize: "0.9rem",
        }}
      >
        {Number(row.child_part_process.spare_process_total ?? 0).toFixed(2)}
      </TableCell>
      {isOpenSpareProcessColumn && (
        <>
          {spareColumn
            ?.sort((a, b) => a.id - b.id)
            .map((process) => (
              <TableCell
                key={process.id}
                sx={{
                  minWidth: 120,
                  maxWidth: 120,
                  borderRight: "1px solid #e0e0e0",
                  borderLeft: "1px solid #e0e0e0",
                  textAlign: "right",
                  fontSize: "0.9rem",
                }}
              >
                {Number(
                  row.child_part_process.spare_process.find(
                    (p) => p.ms_spare_process_v1_id === process.id,
                  )?.cost ?? 0,
                ).toFixed(2)}
              </TableCell>
            ))}
          <TableCell
            sx={{
              minWidth: 120,
              maxWidth: 120,
              borderRight: "1px solid #e0e0e0",
              borderLeft: "1px solid #e0e0e0",
              textAlign: "right",
              fontSize: "0.9rem",
            }}
          >
            {formatUnitCost(
              row.child_part_process.spare_process_admin,
              row.child_part_process.spare_process_admin_unit_type_id,
            )}
          </TableCell>
          <TableCell
            sx={{
              minWidth: 120,
              maxWidth: 120,
              borderRight: "1px solid #e0e0e0",
              borderLeft: "1px solid #e0e0e0",
              textAlign: "right",
              fontSize: "0.9rem",
            }}
          >
            {formatUnitCost(
              row.child_part_process.spare_process_profit,
              row.child_part_process.spare_process_profit_unit_type_id,
            )}
          </TableCell>
        </>
      )}
      <TableCell
        sx={{
          minWidth: 120,
          maxWidth: 120,
          borderRight: "1px solid #e0e0e0",
          borderLeft: "1px solid #e0e0e0",
          textAlign: "right",
          fontSize: "0.9rem",
        }}
      >
        {Number(row.child_part_process.oem_price ?? 0).toFixed(2)}
      </TableCell>
      {isOpenOEMPriceColumn && (
        <>
          <TableCell
            sx={{
              minWidth: 120,
              maxWidth: 120,
              borderRight: "1px solid #e0e0e0",
              borderLeft: "1px solid #e0e0e0",
              textAlign: "right",
              fontSize: "0.9rem",
            }}
          >
            {formatUnitCost(
              row.child_part_process.oem_admin,
              row.child_part_process.oem_admin_unit_type_id,
            )}
          </TableCell>
          <TableCell
            sx={{
              minWidth: 120,
              maxWidth: 120,
              borderRight: "1px solid #e0e0e0",
              borderLeft: "1px solid #e0e0e0",
              textAlign: "right",
              fontSize: "0.9rem",
            }}
          >
            {formatUnitCost(
              row.child_part_process.oem_profit,
              row.child_part_process.oem_profit_unit_type_id,
            )}
          </TableCell>
        </>
      )}
      <TableCell
        sx={{
          minWidth: 120,
          maxWidth: 120,
          borderRight: "1px solid #e0e0e0",
          borderLeft: "1px solid #e0e0e0",
          textAlign: "right",
          fontSize: "0.9rem",
        }}
      >
        {Number(row.child_part_process.spare_price ?? 0).toFixed(2)}
      </TableCell>
      {isOpenSparePriceColumn && (
        <>
          <TableCell
            sx={{
              minWidth: 120,
              maxWidth: 120,
              borderRight: "1px solid #e0e0e0",
              borderLeft: "1px solid #e0e0e0",
              textAlign: "right",
              fontSize: "0.9rem",
            }}
          >
            {formatUnitCost(
              row.child_part_process.spare_admin,
              row.child_part_process.spare_admin_unit_type_id,
            )}
          </TableCell>
          <TableCell
            sx={{
              minWidth: 120,
              maxWidth: 120,
              borderRight: "1px solid #e0e0e0",
              borderLeft: "1px solid #e0e0e0",
              textAlign: "right",
              fontSize: "0.9rem",
            }}
          >
            {formatUnitCost(
              row.child_part_process.spare_profit,
              row.child_part_process.spare_profit_unit_type_id,
            )}
          </TableCell>
        </>
      )}
    </>
  );
};

export default ChildPartProcessData;
